/**
 * SpokeRestrictionToast
 *
 * Listens to `subscriptionEvents.spokeRestriction` (emitted by the API client
 * when a 403 is returned on Spoke) and shows a single coalesced toast via the
 * existing notification system.
 *
 * Two layers of noise control:
 *   1. Per-endpoint suppression — the same endpoint will not toast again within
 *      DEDUPE_WINDOW_MS. Page-load bursts (where N Hub-only endpoints fire
 *      simultaneously) collapse into a single message.
 *   2. Burst coalescing — events arriving within COALESCE_WINDOW_MS are
 *      buffered and emitted as one toast naming the count and the first few
 *      endpoints, instead of N stacked toasts.
 *
 * Mount once inside MainLayout alongside ToastContainer.
 */

import { useEffect, useRef } from 'react'
import { subscriptionEvents } from '@/services/api'
import { useUIStore } from '@/store'

const DEDUPE_WINDOW_MS = 60_000
const COALESCE_WINDOW_MS = 600
const TOAST_DURATION_MS = 4000

export default function SpokeRestrictionToast() {
  const addNotification = useUIStore((s) => s.addNotification)

  const seenAtRef = useRef<Map<string, number>>(new Map())
  const bufferRef = useRef<Array<{ endpoint: string; message: string }>>([])
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const flush = () => {
      flushTimerRef.current = null
      const buffered = bufferRef.current
      bufferRef.current = []
      if (buffered.length === 0) return

      const endpoints = Array.from(new Set(buffered.map((b) => b.endpoint).filter(Boolean)))
      const firstMessage = buffered[0]?.message || 'Endpoint not available on Spoke deployment'

      let title = 'Hub-Only Feature'
      let message = firstMessage
      if (buffered.length > 1) {
        title = `Hub-Only — ${endpoints.length} endpoint${endpoints.length === 1 ? '' : 's'} unavailable`
        const preview = endpoints.slice(0, 3).join(', ')
        const more = endpoints.length > 3 ? ` and ${endpoints.length - 3} more` : ''
        message = `Restricted on Spoke deployment: ${preview}${more}`
      }

      addNotification({
        type: 'warning',
        title,
        message,
        duration: TOAST_DURATION_MS,
      })
    }

    const handler = (...args: unknown[]) => {
      const data = (args[0] ?? {}) as Record<string, unknown>
      const endpoint =
        typeof data.endpoint === 'string' && data.endpoint.length > 0
          ? data.endpoint
          : '__unknown__'
      const message =
        typeof data.message === 'string'
          ? data.message
          : 'This feature is available on Hub deployment only'

      const now = Date.now()
      const seenAt = seenAtRef.current.get(endpoint)
      if (seenAt && now - seenAt < DEDUPE_WINDOW_MS) {
        return // already toasted for this endpoint recently
      }
      seenAtRef.current.set(endpoint, now)

      // Opportunistic GC so the dedupe map can't grow unbounded
      if (seenAtRef.current.size > 64) {
        for (const [k, t] of seenAtRef.current) {
          if (now - t > DEDUPE_WINDOW_MS) seenAtRef.current.delete(k)
        }
      }

      bufferRef.current.push({ endpoint, message })
      if (flushTimerRef.current === null) {
        flushTimerRef.current = setTimeout(flush, COALESCE_WINDOW_MS)
      }
    }

    subscriptionEvents.on('spokeRestriction', handler)
    return () => {
      subscriptionEvents.off('spokeRestriction', handler)
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current)
        flushTimerRef.current = null
      }
    }
  }, [addNotification])

  return null
}
